import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { Card, RouteDirectory, Title } from 'src/Administration/Common';
import * as Yup from 'yup';
import YupPassword from 'yup-password';
import styles from './NewUserPageStyles';

YupPassword(Yup);

import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import allPermissionGroup from 'src/api/permission/allPermissionGroup';
import newMediaPermission from 'src/api/permission/newMediaPermission';
import createProfile from 'src/api/profile/createProfile';
import updateProfile from 'src/api/profile/updateProfile';
import { NOT_ADMIN_ROLES, USER_ROLES } from 'src/constants';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';
import { AdminTextInput } from '../Common/input';

const GRID_CONTAINER_SPACING = 4;

interface RoleOption {
  label: string;
  value: string;
}

interface PermissionGroup {
  id: string;
  name: string;
}

interface PermissionGroupNode {
  node: PermissionGroup;
}

interface AllPermissionGroupsData {
  allPermissionGroups: {
    edges: PermissionGroupNode[];
  };
}

interface SelectedPermission extends PermissionGroup {
  canEdit?: boolean;
  canDelete?: boolean;
  canDownload?: boolean;
  canShare?: boolean;
  hires?: boolean;
}

interface UserFormValues {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  password1: string;
  password2: string;
  phoneNumber?: string;
  companyName?: string;
  role?: string;
}

interface CreateProfileResponse {
  createProfile: {
    profile: {
      id: string;
    };
  };
}

interface CustomCheckBoxProps {
  label: string;
  checked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

interface GroupPermissionsProps {
  name: string;
  canEdit?: boolean;
  canDelete?: boolean;
  canShare?: boolean;
  canDownload?: boolean;
  hires?: boolean;
  onSelectPermissionSetting: (name: string, checked: boolean) => void;
  onClickRemove: () => void;
}

interface PermissionProps {
  selectedPermissions: SelectedPermission[];
  setSelectedPermissions: React.Dispatch<React.SetStateAction<SelectedPermission[]>>;
}

interface PermissionItemProps {
  label: string;
  onClick: () => void;
}

interface PermissionListSearchProps {
  groupsList: AllPermissionGroupsData | undefined;
  setSelectedPermissions: React.Dispatch<React.SetStateAction<SelectedPermission[]>>;
  selectedPermissions: SelectedPermission[];
}

export default function NewUserPage(): React.ReactElement {
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);
  const [selectedPermissions, setSelectedPermissions] = useState<SelectedPermission[]>([]);
  const [createNewUser] = useMutation<CreateProfileResponse>(createProfile);
  const [updateProfileReq] = useMutation(updateProfile);
  const [createMediaPermission] = useMutation(newMediaPermission);
  const navigate = useNavigate();
  const { toastr } = useCustomToast();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const formik = useFormik<UserFormValues>({
    initialValues: {
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      password1: '',
      password2: '',
      role: undefined,
    },
    validationSchema: Yup.object({
      username: Yup.string().required(''),
      firstName: Yup.string().required(''),
      lastName: Yup.string().required(''),
      email: Yup.string().email('Invalid email format').required(''),
      password1: Yup.string()
        .required('')
        .min(
          8,
          'password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special',
        )
        .minLowercase(1, 'password must contain at least 1 lower case letter')
        .minUppercase(1, 'password must contain at least 1 upper case letter')
        .minNumbers(1, 'password must contain at least 1 number')
        .minSymbols(1, 'password must contain at least 1 special character'),
      password2: Yup.string()
        .required('')
        .oneOf([Yup.ref('password1'), ''], 'Passwords must match'),
      role: Yup.string(),
    }),
    onSubmit: async (values: UserFormValues, submitProps: FormikHelpers<UserFormValues>) => {
      const { username, firstName, lastName, email, password1, password2, role, phoneNumber, companyName } = values;
      createNewUser({
        variables: {
          username,
          firstName,
          lastName,
          email,
          password1,
          password2,
        },
      })
        .then((res) => {
          if (!res.data) return;
          updateProfileReq({
            variables: {
              profileId: res.data.createProfile.profile.id,
              role,
              phoneNumber,
              companyName,
            },
          })
            .then(() => {
              for (const permission of selectedPermissions) {
                const {
                  canDelete = false,
                  canDownload = false,
                  canEdit = false,
                  canShare = false,
                  hires = false,
                  id,
                } = permission;
                createMediaPermission({
                  variables: {
                    profileId: res.data?.createProfile.profile.id,
                    permissionsGroupId: id,
                    canDelete,
                    canDownload,
                    canEdit,
                    canShare,
                    hires,
                  },
                });
              }

              toastr.success('User created successfully');
              submitProps.resetForm({ values });
              setHasSubmitted(true);
            })
            .catch((e: Error) => {
              toastr.error(e.message);
            });
        })
        .catch((e: Error) => {
          toastr.error(e.message);
        });
    },
  });

  useEffect(() => {
    if (!formik.dirty && hasSubmitted) {
      navigate({ to: '/systemadmin/users' });
    }
  }, [formik.dirty, hasSubmitted, navigate]);

  const onClickCancel = (): void => {
    navigate({ to: '/systemadmin/users' });
  };

  useEffect(() => {
    if (formik.dirty) {
      window.onbeforeunload = (): boolean => true;
    } else {
      window.onbeforeunload = null;
    }
  }, [formik.dirty]);

  return (
    <Box
      sx={{
        width: '95%',
        margin: '0 auto',
        marginTop: '30px',
      }}
    >
      {/* Note: Navigation blocking handled by window.onbeforeunload set in useEffect below */}
      <form onSubmit={formik.handleSubmit}>
        <Grid container>
          <Grid item xs={12}>
            <RouteDirectory routes={[{ label: 'Users List' }, { label: 'New User' }]} />
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <Card style={{}}>
                <Grid item xs={12}>
                  <Title>New User</Title>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-create-username-input"
                      label={'Username*'}
                      {...formik.getFieldProps('username')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.username}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-create-first-name-input"
                      label={'First Name*'}
                      {...formik.getFieldProps('firstName')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.firstName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-create-last-name-input"
                      label={'Last Name*'}
                      {...formik.getFieldProps('lastName')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.lastName}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput id="cyp-create-email-input" label={'Email*'} {...formik.getFieldProps('email')} />
                    <Typography className={classes.fieldErrors}>{formik.errors.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <div className={classes.cardTitle}>Role</div>
                    <FormControl fullWidth className={classes.selectBox}>
                      <Select
                        labelId="demo-simple-select-label"
                        id="cyp-create-role-input"
                        {...formik.getFieldProps('role')}
                      >
                        {((viewState.signedInUserRole === 'ADM' ? USER_ROLES : NOT_ADMIN_ROLES) as RoleOption[]).map(
                          ({ label, value }) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-create-phone-input"
                      label={'Phone'}
                      {...formik.getFieldProps('phoneNumber')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-create-company-name-input"
                      label={'Company Name'}
                      {...formik.getFieldProps('companyName')}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-create-password-input"
                      label={`Password* (Must contain 8 or more characters with at least one of each: uppercase, lowercase,
                      number and special)`}
                      type={'password'}
                      {...formik.getFieldProps('password1')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.password1}</Typography>
                  </Grid>
                  <Grid className={classes.confirmPassword} item xs={6}>
                    <AdminTextInput
                      id="cyp-create-confirm-password-input"
                      label={'Confirm Password*'}
                      type={'password'}
                      {...formik.getFieldProps('password2')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.password2}</Typography>
                  </Grid>
                </Grid>
                {formik.getFieldProps('role').value === 'USR' && (
                  <Grid container spacing={GRID_CONTAINER_SPACING}>
                    <Grid
                      item
                      xs={12}
                      style={{
                        paddingBottom: '0',
                      }}
                    >
                      <div className={classes.cardTitle}>Permission</div>
                    </Grid>
                    <Permission
                      selectedPermissions={selectedPermissions}
                      setSelectedPermissions={setSelectedPermissions}
                    />
                  </Grid>
                )}
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid className={classes.cancelSaveButtons} item xs={12}>
                    <Button
                      id="cyp-create-user-save-button"
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={Object.keys(formik.errors).length > 0}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={onClickCancel}
                      color="secondary"
                      style={{
                        marginLeft: '10px',
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  {Object.keys(formik.errors).length > 0 && (
                    <Typography className={classes.requiredText}>* required</Typography>
                  )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

const CustomCheckBox = ({ label, ...rest }: CustomCheckBoxProps): React.ReactElement => {
  const classes = styles();
  return (
    <FormControlLabel
      className={classes.checkBoxFormControl}
      sx={{ color: 'text.primary' }}
      control={<Checkbox sx={{ color: 'text.primary', '&.Mui-checked': { color: 'primary.main' } }} {...rest} />}
      label={label}
    />
  );
};

const GroupPermissions = ({
  name,
  canEdit,
  canDelete,
  canShare,
  canDownload,
  hires,
  onSelectPermissionSetting,
  onClickRemove,
}: GroupPermissionsProps): React.ReactElement => {
  const classes = styles();

  const onChangeSetting = (settingName: string, e: ChangeEvent<HTMLInputElement>): void => {
    onSelectPermissionSetting(settingName, e.target.checked);
  };

  return (
    <div className={classes.groupPermissionWrapper}>
      <div className={classes.groupPermissionItem}>
        <div className={classes.groupPermissionItemTop}>
          <div className={classes.groupPermissionItemTitle}>{name}</div>
          <button
            type="button"
            className={classes.groupPermissionItemRemoveButton}
            onClick={onClickRemove}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            Remove
          </button>
        </div>
        <div>
          <CustomCheckBox label="Edit" checked={canEdit} onChange={(e) => onChangeSetting('canEdit', e)} />
          <CustomCheckBox label="Share" checked={canShare} onChange={(e) => onChangeSetting('canShare', e)} />
          <CustomCheckBox label="Download" checked={canDownload} onChange={(e) => onChangeSetting('canDownload', e)} />
          <CustomCheckBox label="Delete" checked={canDelete} onChange={(e) => onChangeSetting('canDelete', e)} />
          <CustomCheckBox label="Request Hires" checked={hires} onChange={(e) => onChangeSetting('hires', e)} />
        </div>
      </div>
    </div>
  );
};

const Permission = ({ selectedPermissions, setSelectedPermissions }: PermissionProps): React.ReactElement => {
  const { data: groupsList } = useQuery<AllPermissionGroupsData>(allPermissionGroup, {
    fetchPolicy: 'network-only',
  });

  const onSelectPermissionSetting = (id: string, name: string, val: boolean): void => {
    setSelectedPermissions((prev) => prev.map((a) => (a.id === id ? { ...a, [name]: val } : a)));
  };

  const onClickRemove = (id: string): void => {
    setSelectedPermissions((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <>
      <Grid item xs={6}>
        <PermissionListSearch
          groupsList={groupsList}
          setSelectedPermissions={setSelectedPermissions}
          selectedPermissions={selectedPermissions}
        />
      </Grid>
      <Grid
        item
        xs={6}
        style={{
          overflowY: 'scroll',
        }}
      >
        {selectedPermissions.map(({ name, id, canEdit, canDelete, canShare, canDownload, hires }) => (
          <GroupPermissions
            key={id}
            name={name}
            canEdit={canEdit}
            canDelete={canDelete}
            canShare={canShare}
            canDownload={canDownload}
            hires={hires}
            onClickRemove={() => onClickRemove(id)}
            onSelectPermissionSetting={(settingName, val) => onSelectPermissionSetting(id, settingName, val)}
          />
        ))}
      </Grid>
    </>
  );
};

const PermissionItem = ({ label, onClick }: PermissionItemProps): React.ReactElement => {
  const classes = styles();
  return (
    <button
      type="button"
      className={classes.checkBoxFormControl}
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        width: '100%',
        textAlign: 'left',
      }}
    >
      {label}
    </button>
  );
};

const PermissionListSearch = ({
  groupsList,
  setSelectedPermissions,
  selectedPermissions,
}: PermissionListSearchProps): React.ReactElement => {
  const classes = styles();
  const [searchVal, setSearchVal] = useState('');

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setSearchVal(e.target.value);
  };

  const onSelect = (node: PermissionGroup): void => {
    setSelectedPermissions((prev) => [...prev, node]);
  };

  return (
    <div className={classes.permissionContainer}>
      <div className={classes.permissionSearchBox}>
        <InputBase
          className={classes.searchInput}
          placeholder={'Quick Search'}
          value={searchVal}
          onChange={onChangeSearch}
        />
      </div>
      <div className={classes.permissionListContainer}>
        {groupsList?.allPermissionGroups.edges
          .filter((a) => !selectedPermissions.find(({ id }) => id === a.node.id))
          .filter((a) => a.node.name.toUpperCase().includes(searchVal.toUpperCase()))
          .map((a) => (
            <PermissionItem key={a.node.id} label={a.node.name} onClick={() => onSelect(a.node)} />
          ))}
      </div>
    </div>
  );
};
